import { type RefObject, useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { HeaderWithDescription } from '../HeaderWithDescription';
import { type BoxData, type OurBoxesPageData } from '../../lib/types';
import { scrollToRef } from '../../lib/utils/scroll-to-ref';
import styles from '../../styles/OurBoxes.module.css';
import { AgeGroupImageButtonAlt } from './AgeGroupImageButtonAlt';
import { BoxCardList } from './BoxCardList';

type OurBoxesFlowQuestionaireProps = {
  readonly page: OurBoxesPageData;
  readonly boxes: BoxData[];
  readonly onOpenBox: (boxUrl: string) => void;
};

enum AgeGroup {
  GROUP_0_TO_1 = 'GROUP_0_TO_1',
  GROUP_1_TO_2 = 'GROUP_1_TO_2',
  GROUP_2_TO_3 = 'GROUP_2_TO_3',
  GROUP_3_TO_6 = 'GROUP_3_TO_6',
}
type AgeGroupState = AgeGroup | false;

function showHideContinueOnScroll(
  divReference: RefObject<HTMLDivElement>,
  setShowFloating: (boolean) => void
) {
  const scrollHeight = window.scrollY;
  const screenHeight = window.innerHeight;

  if (
    scrollHeight + screenHeight - 50 <
    (divReference?.current?.offsetTop ?? 0) +
      (divReference?.current?.clientHeight ?? 0)
  ) {
    setShowFloating(true);
  } else {
    setShowFloating(false);
  }
}

export function OurBoxesFlowQuestionaire(props: OurBoxesFlowQuestionaireProps) {
  const { page, boxes, onOpenBox } = props;

  const [step, setStep] = useState(1);
  const [step1SelectedItem, setStep1SelectedItem] = useState<number>(0);
  const [step2SelectedItem, setStep2SelectedItem] = useState<number>(0);
  const [ageGroup, setAgeGroup] = useState<AgeGroupState>(false);
  const [step1ShowFloating, setStep1ShowFloating] = useState(true);
  const [step2ShowFloating, setStep2ShowFloating] = useState(true);

  const step1ContinueButton = useRef<HTMLDivElement>(null);
  const step2ContinueButton = useRef<HTMLDivElement>(null);
  const pageTopRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      showHideContinueOnScroll(step1ContinueButton, setStep1ShowFloating);
      showHideContinueOnScroll(step2ContinueButton, setStep2ShowFloating);
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleStep1NextClick = () => {
    if (step1SelectedItem === 1) {
      setStep(2);
      scrollToRef(pageTopRef);
    } else if (step1SelectedItem === 2) {
      setStep(3);
      setAgeGroup(AgeGroup.GROUP_3_TO_6);
    }
  };

  const handleStep2NextClick = () => {
    switch (step2SelectedItem) {
      case 1: {
        setStep(3);
        setAgeGroup(AgeGroup.GROUP_0_TO_1);
        break;
      }

      case 2: {
        setStep(3);
        setAgeGroup(AgeGroup.GROUP_1_TO_2);
        break;
      }

      case 3: {
        setStep(3);
        setAgeGroup(AgeGroup.GROUP_2_TO_3);
        break;
      }
      // No default
    }
  };

  const step1ContinueSection = (
    <Row className="justify-content-center">
      <Col xs={12} md={6} lg={4} className="text-center">
        <Button
          size="lg"
          className="w-100"
          disabled={step1SelectedItem === 0}
          onClick={handleStep1NextClick}
        >
          {page.step2Continue} <FontAwesomeIcon icon={faArrowRight} />
        </Button>
      </Col>
    </Row>
  );

  const step1 = (
    <Container className="py-5">
      <HeaderWithDescription
        title={page.step1TitleAlt}
        className="text-center"
      />
      <Row className="mt-5" xs={1} md={2}>
        <Col className="mb-5">
          <AgeGroupImageButtonAlt
            isSelected={step1SelectedItem === 1}
            title={page.step1AgeGroup0to3Alt}
            image={page.step1AgeGroup0to3ImageAlt}
            onClick={() => {
              setStep1SelectedItem(1);
            }}
          />
        </Col>
        <Col className="mb-5">
          <AgeGroupImageButtonAlt
            isSelected={step1SelectedItem === 2}
            title={page.step1AgeGroup3to6Alt}
            image={page.step1AgeGroup3to6ImageAlt}
            onClick={() => {
              setStep1SelectedItem(2);
            }}
          />
        </Col>
      </Row>
      <div ref={step1ContinueButton}>{step1ContinueSection}</div>

      {step1ShowFloating && (
        <div
          className={`${styles.continueButtonFloating} justify-content-center w-100 pt-2 px-4 d-block d-md-none`}
        >
          {step1ContinueSection}
        </div>
      )}
    </Container>
  );

  const step2ContinueSection = (
    <Row className="justify-content-center">
      <Col xs={12} md={6} lg={4} className="text-center">
        <Button
          size="lg"
          className="w-100"
          disabled={step2SelectedItem === 0}
          onClick={handleStep2NextClick}
        >
          {page.step2Continue} <FontAwesomeIcon icon={faArrowRight} />
        </Button>
      </Col>
    </Row>
  );

  const step2 = (
    <Container className="py-5">
      <HeaderWithDescription
        title={page.step2TitleAlt}
        className="text-center"
      />
      <Row className="mt-5" xs={1} md={3}>
        <Col className="mb-5">
          <AgeGroupImageButtonAlt
            isSelected={step2SelectedItem === 1}
            title={page.step2AgeGroup0to1Alt}
            image={page.step2AgeGroup0to1ImageAlt}
            onClick={() => {
              setStep2SelectedItem(1);
            }}
          />
        </Col>
        <Col className="mb-5">
          <AgeGroupImageButtonAlt
            isSelected={step2SelectedItem === 2}
            title={page.step2AgeGroup1to2Alt}
            image={page.step2AgeGroup1to2ImageAlt}
            onClick={() => {
              setStep2SelectedItem(2);
            }}
          />
        </Col>
        <Col className="mb-5">
          <AgeGroupImageButtonAlt
            isSelected={step2SelectedItem === 3}
            title={page.step2AgeGroup2to3Alt}
            image={page.step2AgeGroup2to3ImageAlt}
            onClick={() => {
              setStep2SelectedItem(3);
            }}
          />
        </Col>
      </Row>
      <div ref={step2ContinueButton}>{step2ContinueSection}</div>

      {step2ShowFloating && (
        <div
          className={`${styles.continueButtonFloating} justify-content-center w-100 pt-2 px-4 d-block d-md-none`}
        >
          {step2ContinueSection}
        </div>
      )}
    </Container>
  );

  const step3 = (
    <AllBoxesList
      boxes={boxes}
      page={page}
      selectedAgeGroup={ageGroup}
      onOpenBox={onOpenBox}
    />
  );

  return (
    <div ref={pageTopRef}>
      {step === 1 ? step1 : step === 2 ? step2 : step === 3 ? step3 : null}
    </div>
  );
}

type AllBoxesListProps = {
  readonly boxes: BoxData[];
  readonly page: OurBoxesPageData;
  readonly onOpenBox: (boxUrl: string) => void;
  readonly selectedAgeGroup: AgeGroupState;
};

function AllBoxesList(props: AllBoxesListProps) {
  const { boxes, page, onOpenBox, selectedAgeGroup } = props;

  const boxes0to12mthRef = useRef(null);
  const boxes1to2yrRef = useRef(null);
  const boxes2to3yrRef = useRef(null);
  const boxes3to6yrRef = useRef(null);

  useEffect(() => {
    switch (selectedAgeGroup) {
      case AgeGroup.GROUP_0_TO_1: {
        scrollToRef(boxes0to12mthRef);
        break;
      }

      case AgeGroup.GROUP_1_TO_2: {
        scrollToRef(boxes1to2yrRef);
        break;
      }

      case AgeGroup.GROUP_2_TO_3: {
        scrollToRef(boxes2to3yrRef);
        break;
      }

      case AgeGroup.GROUP_3_TO_6: {
        scrollToRef(boxes3to6yrRef);
        break;
      }

      default: {
        scrollToRef(boxes0to12mthRef);
        break;
      }
    }
  }, [selectedAgeGroup]);

  return (
    <div className={styles.boxListContainer}>
      <Container className="text-align py-4">
        <div ref={boxes0to12mthRef}>
          <BoxCardList
            boxes={boxes.filter(
              (box) => box.ageGroup && box.ageGroup.maxMonths <= 12
            )}
            title={page.step2AgeGroup0to1}
            description={page.step2AgeGroup0to1Description}
            onOpenBox={onOpenBox}
          />
        </div>
        <div ref={boxes1to2yrRef}>
          <BoxCardList
            boxes={boxes.filter(
              (box) =>
                box.ageGroup &&
                box.ageGroup.minMonths >= 12 &&
                box.ageGroup.maxMonths <= 24
            )}
            title={page.step2AgeGroup1to2}
            description={page.step2AgeGroup1to2Description}
            onOpenBox={onOpenBox}
          />
        </div>
        <div ref={boxes2to3yrRef}>
          <BoxCardList
            boxes={boxes.filter(
              (box) =>
                box.ageGroup &&
                box.ageGroup.minMonths >= 24 &&
                box.ageGroup.maxMonths <= 36
            )}
            title={page.step2AgeGroup2to3}
            description={page.step2AgeGroup2to3Description}
            onOpenBox={onOpenBox}
          />
        </div>
        <div ref={boxes3to6yrRef}>
          <BoxCardList
            boxes={boxes.filter(
              (box) => box.ageGroup && box.ageGroup.minMonths >= 36
            )}
            title={page.step2AgeGroup3to6}
            description={page.step2AgeGroup3to6Description}
            onOpenBox={onOpenBox}
          />
        </div>
      </Container>
    </div>
  );
}
